import {
  type LearnCardAction,
  type LearnCardActionReceipt,
  type LearnCardActionTrigger,
  type LearnCardExitDirection,
  learnCardItemKey,
  learnCardPresentationKey,
} from "@-/dev.improve.haniverse.lib.learn.card-action";
import {
  type LearnCardDestination,
  type LearnCardDestinationResolution,
  learnCardDestinationKey,
} from "@-/dev.improve.haniverse.lib.learn.destination";

import {
  flashcardQueueSuccessor,
  flashcardQueueTailKey,
  isFlashcardQueueEntryReady,
} from "#flashcard-queue";
import type {
  FlashcardQueueEntry,
  LearnCardEndResolution,
  LearnCardFace,
  LearnCardViewMode,
  LearnDeckSlot,
  LearnDeckState,
  LearnDeckSurface,
  LearnDeckSurfaceReadiness,
} from "#learn-deck-state";

type CardItem = LearnDeckSlot["item"];

export type LearnDeckEvent =
  | {
      /**
       * Undo bringing back the card before the one on show: the card on show goes back underneath
       * it as the head of the restored queue (it is what followed the restored card), usually still
       * mounted, so nothing is drawn in between.
       */
      retainShown?: boolean;
      state: LearnDeckState;
      type: "restore-history";
    }
  /**
   * What the queue was planned from changed outside the deck (a setting, the review focus, another
   * device's study): drop the queued flashcards so they are planned again. Only at rest, before
   * anything has uncovered them; the controller holds it until then.
   */
  | { type: "queue-invalidated" }
  /** Keep the current pair and successor front while another tab is focused. */
  | { type: "trim-preloads" }
  /** The current controller/draft survives, but its native layouts must be earned again. */
  | { presentationKey: string; type: "release-current-surfaces" }
  /**
   * The flashcard after the queue's tail (`afterKey`, a presentation key) was resolved. Appended
   * while that is still the tail; dropped otherwise. It never replaces a queued flashcard.
   */
  | { afterKey: string; resolution: LearnCardDestinationResolution; type: "flashcard-enqueued" }
  | { direction: "left" | "right"; type: "park-drag" }
  /** A card surface reported its first real layout. */
  | { presentationKey: string; surface: LearnDeckSurface; type: "surface-ready" }
  | { type: "drag-start" }
  | { type: "drag-cancel" }
  | {
      action: LearnCardAction;
      direction: LearnCardExitDirection;
      operationId: string;
      trigger: LearnCardActionTrigger;
      type: "commit";
    }
  | { operationId: string; receipt: LearnCardActionReceipt; type: "write-saved" }
  /** The write was refused before anything landed (the free-tier gate); the card stays. */
  | { operationId: string; type: "write-blocked" }
  /** The write rolled back: nothing landed, so the card is actionable again. */
  | { operationId: string; type: "write-failed" }
  /** The write may have landed (a lost commit acknowledgement): only it may be retried. */
  | { operationId: string; type: "write-outcome-unknown" }
  /** The next flashcard did not become ready in time. */
  | { operationId: string; type: "destination-failed" }
  /** Load the SAME next flashcard again, under a fresh mount. */
  | { operationId: string; type: "retry-destination" }
  /** The saved write was rolled back while its successor failed to load; the card is idle again. */
  | { operationId: string; type: "saved-action-undone" }
  /** Retry the same write identity; its previous outcome may have been lost. */
  | { type: "recover" }
  | { operationId: string; type: "exit-started" }
  | { operationId: string; type: "exit-finished" }
  | { operationId: string; type: "departure-finished" }
  | { to: LearnCardFace; type: "flip" }
  | { type: "flip-finished" }
  /**
   * `face` lands the STUDY surface on that face when the switch completes — the upward return
   * from Info opens the mnemonics (answer) face; "Back to card" (down) keeps the face the
   * learner left. Applied at once so the surface under the lifting Info already shows it.
   */
  | { face?: LearnCardFace; to: LearnCardViewMode; type: "switch-view" }
  | { type: "switch-view-finished" }
  /** Show `item` as a fresh session (entry, undo, a jump to another card). */
  | { item: CardItem; sessionId: string; type: "reset-session" };

const NOT_READY = { back: false, front: false, info: false } as const;

/**
 * Whether `item` ever mounts `surface`. A slide or a quiz has a single face and no Info view, so
 * nothing will ever report a layout for its `back` or `info`: those surfaces are ready by
 * construction.
 */
function rendersSurface(item: CardItem, surface: LearnDeckSurface): boolean {
  return (item.kind !== "slide" && item.kind !== "quiz") || surface === "front";
}

/**
 * `ready` with every surface the item never renders held at `true`. Readiness resets (trimming
 * preloads, releasing the current card's layouts, restoring history) clear surfaces wholesale;
 * clearing one that is never mounted strands the slot forever. That is how a committed slide
 * "Next" once spun until the loading timeout: trimming reset the prepared slide's `back`, and the
 * slot could then never report ready.
 */
function withUnrenderedSurfacesReady(item: CardItem, ready: LearnDeckSurfaceReadiness) {
  const surfaces: readonly LearnDeckSurface[] = ["back", "front", "info"];
  return surfaces.every((surface) => ready[surface] || rendersSurface(item, surface))
    ? ready
    : {
        back: ready.back || !rendersSurface(item, "back"),
        front: ready.front || !rendersSurface(item, "front"),
        info: ready.info || !rendersSurface(item, "info"),
      };
}

function withSlotReadinessInvariant(slot: LearnDeckSlot): LearnDeckSlot {
  const ready = withUnrenderedSurfacesReady(slot.item, slot.ready);
  return ready === slot.ready ? slot : { ...slot, ready };
}

/** `queue` with `update` applied to each card slot; the same array when no slot changed. */
function mapQueueSlots(
  queue: LearnDeckState["queue"],
  update: (slot: LearnDeckSlot, index: number) => LearnDeckSlot,
): LearnDeckState["queue"] {
  let changed = false;
  const next = queue.map((entry, index): FlashcardQueueEntry => {
    if (entry.kind !== "card") {
      return entry;
    }
    const slot = update(entry.slot, index);
    if (slot === entry.slot) {
      return entry;
    }
    changed = true;
    return { ...entry, slot };
  });
  return changed ? next : queue;
}

/**
 * Applied to every state the reducer returns, so no event — present or future — can leave a slot
 * waiting on a surface it never mounts. Returns `state` itself when nothing needed fixing.
 */
function withReadinessInvariant(state: LearnDeckState): LearnDeckState {
  const current = withSlotReadinessInvariant(state.current);
  const queue = mapQueueSlots(state.queue, withSlotReadinessInvariant);
  return current === state.current && queue === state.queue ? state : { ...state, current, queue };
}

function makeSlot(item: CardItem, sessionId: string, occurrence: number): LearnDeckSlot {
  return {
    item,
    presentation: { item, occurrence, sessionId },
    ready: withUnrenderedSurfacesReady(item, { ...NOT_READY }),
  };
}

function itemForDestination(destination: LearnCardDestination): CardItem | null {
  switch (destination.kind) {
    case "slide": {
      return { kind: "slide", slideSlug: destination.lesson.slug };
    }
    case "quiz": {
      return { kind: "quiz", quizSlug: destination.quizSlug, source: destination.source };
    }
    case "review": {
      return {
        drillType: destination.card.drillType,
        grapheme: destination.card.grapheme,
        kind: "review",
      };
    }
    case "lesson": {
      return {
        grapheme: destination.lesson.graphemeCard.front,
        kind: "lesson",
        levelIndex: destination.lesson.level.index,
        ...(destination.lesson.part === undefined ? {} : { part: destination.lesson.part }),
        ...(destination.lesson.pinyin === undefined ? {} : { pinyin: destination.lesson.pinyin }),
      };
    }
    default: {
      return null;
    }
  }
}

function isEndResolution(
  resolution: LearnCardDestinationResolution,
): resolution is LearnCardEndResolution {
  return resolution.destination.kind === "summary" || resolution.destination.kind === "exit";
}

export function initialLearnDeckState({
  face = "front",
  item,
  sessionId,
  view = "study",
}: {
  face?: LearnCardFace;
  item: CardItem;
  sessionId: string;
  view?: LearnCardViewMode;
}): LearnDeckState {
  return {
    current: makeSlot(item, sessionId, 1),
    departing: null,
    face,
    nextOccurrence: 2,
    previous: null,
    queue: [],
    sessionId,
    transition: { phase: "idle" },
    view,
  };
}

/**
 * The nonblank-transition invariant, as a predicate: a committed card may finish leaving only once
 * its write has landed AND the next flashcard in the queue is ready — never on either alone. Touch
 * ownership is checked atomically by the UI-thread motion driver, never by a delayed JS mirror.
 */
export function canFinishLearnCardExit(state: LearnDeckState): boolean {
  const { transition } = state;
  return (
    transition.phase === "committed" &&
    transition.writeStatus === "saved" &&
    transition.destinationStatus === "pending" &&
    isFlashcardQueueEntryReady(flashcardQueueSuccessor(state))
  );
}

/**
 * The committed operation whose write or loading failed. Its card must rest at neutral rather than
 * at its parked offset: the learner is shown an error, and a later drag must not snap it back to
 * the edge it was committed toward.
 */
export function failedLearnCardOperationId(state: LearnDeckState): string | null {
  const { transition } = state;
  return transition.phase === "committed" &&
    (transition.writeStatus === "unknown" || transition.destinationStatus === "failed")
    ? transition.operationId
    : null;
}

function withDestinationStatus(
  state: LearnDeckState,
  operationId: string,
  destinationStatus: "failed" | "pending",
): LearnDeckState {
  const { transition } = state;
  if (transition.phase !== "committed" || transition.operationId !== operationId) {
    return state;
  }
  return { ...state, transition: { ...transition, destinationStatus } };
}

/**
 * The deck's whole logical transition function. Pure, so every ordering the real world can
 * produce — a write landing before or after readiness, a stale resolution arriving after a
 * promotion, a stale callback from an earlier operation — is a unit test rather than a device
 * session.
 *
 * The flashcard queue (`state.queue`) only ever grows at its tail (`flashcard-enqueued`) and
 * shrinks at its head (a promotion). A committed action therefore always lands on the card that
 * was preloaded under it: no event can swap a queued flashcard for a different one.
 *
 * Every event that names an `operationId` or `presentationKey` is dropped when it does not match
 * the live one: completion events are tied to the attempt they belong to, and a late callback from
 * an abandoned attempt cannot advance the deck twice.
 */
export function learnDeckReducer(state: LearnDeckState, event: LearnDeckEvent): LearnDeckState {
  return withReadinessInvariant(reduceLearnDeckEvent(state, event));
}

function reduceLearnDeckEvent(state: LearnDeckState, event: LearnDeckEvent): LearnDeckState {
  switch (event.type) {
    case "restore-history": {
      let nextOccurrence = Math.max(state.nextOccurrence, event.state.nextOccurrence);
      const mountedSlots = [
        state.current,
        ...state.queue.flatMap((entry) => (entry.kind === "card" ? [entry.slot] : [])),
        // Undo during a flight returns to the card that is still flying off: it stays mounted.
        state.departing?.slot ?? null,
        // After it, to the card whose flight landed, still mounted off the stage.
        state.previous?.slot ?? null,
      ];
      const findMounted = (slot: LearnDeckSlot) =>
        mountedSlots.find(
          (mounted) => mounted !== null && presentationKeyOf(mounted) === presentationKeyOf(slot),
        );
      // The frame's queue is restored as it was: undo and redo put the data back where it was
      // when the frame was recorded, so its flashcards are still the ones that follow. History
      // retains logical slots, not views: a queued card that is no longer mounted earns its
      // readiness again, under a fresh identity that rejects callbacks from its previous mount.
      const queue = event.state.queue.map((entry): FlashcardQueueEntry => {
        if (entry.kind !== "card") {
          return entry;
        }
        const slot =
          findMounted(entry.slot) ??
          makeSlot(entry.slot.item, event.state.sessionId, nextOccurrence++);
        return slot === entry.slot ? entry : { ...entry, slot };
      });
      // The history entry preserves card/face identity, not a layout lease. A current view that
      // was released or replaced must report its new layouts before actions can resume.
      const currentReady =
        findMounted(event.state.current)?.ready ??
        makeSlot(event.state.current.item, event.state.sessionId, 0).ready;
      const current =
        event.state.current.ready.front === currentReady.front &&
        event.state.current.ready.back === currentReady.back &&
        event.state.current.ready.info === currentReady.info
          ? event.state.current
          : { ...event.state.current, ready: currentReady };
      // Undo slides the restored card in over the card on show, which must stay drawn beneath it
      // the whole way. That card is what followed the restored one, so it heads the restored
      // queue: the frame's own queue already starts with it, unless the action was taken before the
      // next flashcard had been queued.
      const shown = state.current;
      const shownKey = presentationKeyOf(shown);
      const [restoredSuccessor] = queue;
      const restoredQueue =
        event.retainShown === true &&
        shownKey !== presentationKeyOf(current) &&
        (restoredSuccessor?.kind !== "card" ||
          presentationKeyOf(restoredSuccessor.slot) !== shownKey)
          ? [{ key: learnCardItemKey(shown.item), kind: "card" as const, slot: shown }]
          : queue;
      return {
        ...event.state,
        current,
        departing: null,
        nextOccurrence,
        previous: null,
        queue: restoredQueue,
        transition: { phase: "idle" },
      };
    }
    case "trim-preloads": {
      if (state.transition.phase !== "idle") {
        return state;
      }
      // The queue keeps its flashcards; only the surfaces mounted beyond the current pair and the
      // successor's front are released, and must report again once Learn is back. The card the
      // last promotion went past is dropped.
      const queue = mapQueueSlots(state.queue, (slot, index) => {
        if (index === 0) {
          return !rendersSurface(slot.item, "back") || !slot.ready.back
            ? slot
            : { ...slot, ready: { ...slot.ready, back: false } };
        }
        // Only the surfaces it renders: one it never mounts is held ready
        // (`withUnrenderedSurfacesReady`). Clearing that too made an equal slot, never the same
        // one, and with it a new queue, which re-ran the trim while Learn was hidden: a queued
        // slide or quiz behind the next card looped until React gave up ("Maximum update depth").
        const trimmed = withUnrenderedSurfacesReady(slot.item, { ...NOT_READY });
        return slot.ready.front === trimmed.front &&
          slot.ready.back === trimmed.back &&
          slot.ready.info === trimmed.info
          ? slot
          : { ...slot, ready: trimmed };
      });
      return queue === state.queue && state.previous === null
        ? state
        : { ...state, previous: null, queue };
    }
    case "queue-invalidated": {
      // Never while a drag, reveal or committed action is using the card underneath: the one the
      // learner is uncovering, or about to be promoted to, must stay the one they get.
      if (state.transition.phase !== "idle" || state.queue.length === 0) {
        return state;
      }
      return { ...state, queue: [] };
    }
    case "release-current-surfaces": {
      if (
        state.transition.phase !== "idle" ||
        presentationKeyOf(state.current) !== event.presentationKey ||
        (!state.current.ready.front && !state.current.ready.back && !state.current.ready.info)
      ) {
        return state;
      }
      return { ...state, current: { ...state.current, ready: { ...NOT_READY } } };
    }
    case "flashcard-enqueued": {
      if (flashcardQueueTailKey(state) !== event.afterKey) {
        return state;
      }
      const { resolution } = event;
      const key = learnCardDestinationKey(resolution.destination);
      if (isEndResolution(resolution)) {
        return { ...state, queue: [...state.queue, { key, kind: "end", resolution }] };
      }
      const item = itemForDestination(resolution.destination);
      if (item === null) {
        return state;
      }
      return {
        ...state,
        nextOccurrence: state.nextOccurrence + 1,
        queue: [
          ...state.queue,
          { key, kind: "card", slot: makeSlot(item, state.sessionId, state.nextOccurrence) },
        ],
      };
    }
    case "park-drag": {
      return state.transition.phase === "committed"
        ? {
            ...state,
            transition: { ...state.transition, direction: event.direction },
          }
        : state;
    }
    case "reset-session": {
      return initialLearnDeckState({ item: event.item, sessionId: event.sessionId });
    }

    case "surface-ready": {
      const currentKey = presentationKeyOf(state.current);
      if (currentKey === event.presentationKey) {
        if (state.current.ready[event.surface]) {
          return state;
        }
        return {
          ...state,
          current: { ...state.current, ready: { ...state.current.ready, [event.surface]: true } },
        };
      }
      const queue = mapQueueSlots(state.queue, (slot) =>
        presentationKeyOf(slot) !== event.presentationKey || slot.ready[event.surface]
          ? slot
          : { ...slot, ready: { ...slot.ready, [event.surface]: true } },
      );
      return queue === state.queue ? state : { ...state, queue };
    }

    case "drag-start": {
      return state.transition.phase === "idle"
        ? { ...state, transition: { phase: "dragging" } }
        : state;
    }

    case "drag-cancel": {
      return state.transition.phase === "dragging"
        ? { ...state, transition: { phase: "idle" } }
        : state;
    }

    case "commit": {
      const { phase } = state.transition;
      if (phase !== "idle" && phase !== "dragging") {
        return state;
      }
      return {
        ...state,
        transition: {
          action: event.action,
          destinationStatus: "pending",
          direction: event.direction,
          operationId: event.operationId,
          phase: "committed",
          receipt: null,
          trigger: event.trigger,
          writeStatus: "pending",
        },
      };
    }

    case "write-saved": {
      const { transition } = state;
      if (transition.phase !== "committed" || transition.operationId !== event.operationId) {
        return state;
      }
      return {
        ...state,
        transition: { ...transition, receipt: event.receipt, writeStatus: "saved" },
      };
    }

    case "write-blocked":
    case "write-failed":
    case "write-outcome-unknown": {
      const { transition } = state;
      if (transition.phase !== "committed" || transition.operationId !== event.operationId) {
        return state;
      }
      // Blocked or failed: nothing landed, so the card is simply actionable again — with ANY
      // action, not just the one that failed. Unknown: the write may have landed, so the card
      // waits until the learner retries that same operation, which is exactly-once. The queue
      // stays: it already follows this card, whichever action eventually takes it.
      return event.type === "write-outcome-unknown"
        ? { ...state, transition: { ...transition, writeStatus: "unknown" } }
        : { ...state, transition: { phase: "idle" } };
    }

    case "destination-failed": {
      return withDestinationStatus(state, event.operationId, "failed");
    }

    case "retry-destination": {
      const { transition } = state;
      if (
        transition.phase !== "committed" ||
        transition.operationId !== event.operationId ||
        transition.destinationStatus !== "failed"
      ) {
        return state;
      }
      // Retrying LOADING only: the write status is untouched, so a saved write is never replayed.
      // The next flashcard stays the same card; it is mounted again under a fresh identity, which
      // repairs a mount that never reported. An empty queue is extended again by the controller.
      const successor = flashcardQueueSuccessor(state);
      const queue =
        successor?.kind === "card"
          ? [
              {
                ...successor,
                slot: makeSlot(successor.slot.item, state.sessionId, state.nextOccurrence),
              },
              ...state.queue.slice(1),
            ]
          : state.queue;
      return {
        ...state,
        nextOccurrence:
          successor?.kind === "card" ? state.nextOccurrence + 1 : state.nextOccurrence,
        queue,
        transition: { ...transition, destinationStatus: "pending" },
      };
    }

    case "saved-action-undone": {
      const { transition } = state;
      if (
        transition.phase !== "committed" ||
        transition.operationId !== event.operationId ||
        transition.writeStatus !== "saved" ||
        transition.destinationStatus !== "failed"
      ) {
        return state;
      }
      // The write is gone, so the card is back where it was before its action, and so is the
      // queue that follows it.
      return { ...state, transition: { phase: "idle" } };
    }

    case "recover": {
      const { transition } = state;
      if (transition.phase !== "committed" || transition.writeStatus !== "unknown") {
        return state;
      }
      return { ...state, transition: { ...transition, writeStatus: "pending" } };
    }

    case "exit-started": {
      const { transition } = state;
      if (transition.phase !== "committed" || transition.operationId !== event.operationId) {
        return state;
      }
      if (!canFinishLearnCardExit(state)) {
        return state;
      }
      const successor = flashcardQueueSuccessor(state);
      if (successor?.kind === "card") {
        // Promotion, as the exit starts: the head of the queue becomes the current card on its
        // FRONT in study mode (an Info view never carries over) and takes input at once, while
        // this card flies off above it as `departing`. Nothing waits for the flight.
        return {
          ...state,
          current: successor.slot,
          departing: {
            face: state.face,
            operationId: transition.operationId,
            slot: state.current,
            view: state.view,
          },
          face: "front",
          queue: state.queue.slice(1),
          transition: { phase: "idle" },
          view: "study",
        };
      }
      // The end of the queue: the card flies off, then the host navigates (`exit-finished`).
      return {
        ...state,
        transition: {
          action: transition.action,
          direction: transition.direction,
          operationId: transition.operationId,
          phase: "exiting",
          receipt: transition.receipt,
        },
      };
    }

    case "departure-finished": {
      // Landed: kept mounted off the stage as `previous`, in place of the card before it.
      return state.departing?.operationId === event.operationId
        ? { ...state, departing: null, previous: state.departing }
        : state;
    }

    case "exit-finished": {
      const { transition } = state;
      if (transition.phase !== "exiting" || transition.operationId !== event.operationId) {
        return state;
      }
      // Only the end of the queue exits this way (a card is promoted as its exit starts): the deck
      // is being left, and the host navigates. Nothing to promote.
      return { ...state, transition: { phase: "idle" } };
    }

    case "flip": {
      if (state.transition.phase === "dragging" && state.view === "study" && event.to === "back") {
        return { ...state, face: "back" };
      }
      if (state.transition.phase !== "idle" || state.view !== "study" || state.face === event.to) {
        return state;
      }
      return { ...state, transition: { phase: "flipping", to: event.to } };
    }

    case "flip-finished": {
      const { transition } = state;
      if (transition.phase !== "flipping") {
        return state;
      }
      return { ...state, face: transition.to, transition: { phase: "idle" } };
    }

    case "switch-view": {
      if (state.transition.phase !== "idle" || state.view === event.to) {
        return state;
      }
      return {
        ...state,
        face: event.face ?? state.face,
        transition: { phase: "switching-view", to: event.to },
      };
    }

    case "switch-view-finished": {
      const { transition } = state;
      if (transition.phase !== "switching-view") {
        return state;
      }
      return { ...state, transition: { phase: "idle" }, view: transition.to };
    }
  }
}

function presentationKeyOf(slot: LearnDeckSlot): string {
  return learnCardPresentationKey(slot.presentation);
}
